/**
 * OperationsIncidents.tsx — Operations / Incidents (M3).
 *
 * The mockup's Incidents view (klaxiora-crm-mockup-full.html :1938-1959):
 * four KPI cards, an incidents table (Incident / Severity / Related agent /
 * Date / Status, :5087) with "Mark Resolved" on open rows (:5092), and a
 * Log-Incident flow (modal :3089-3128: title, severity, related-agent
 * people select, description). Backed for real by /v1/operations/incidents
 * (0101); the related person is an employee (decision M0), picked from the
 * live active-employee list — the M1/M2 picker pattern. Create-only drawer
 * + row-level resolve: incidents carry NO edit or delete, matching the
 * mockup and 0101's no-DELETE grant.
 *
 * Two deliberate divergences from the mockup, both honesty rules: the
 * avg-resolution KPI is COMPUTED from resolvedAt (the mockup hardcodes
 * "6h — Demo figure", :1954), and the mockup's "Related task" COLUMN stays
 * absent even now that M5a ships the link — a linked incident renders a
 * detail line under its title instead, so unlinked rows never carry a
 * column of permanent dashes. The drawer's "Related task" select
 * (:3114-3117) is live against the real task list (0103's
 * related_task_id).
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import { SectionTitle, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { OpsMetric } from './OperationsOverview'
import { useAsync, fetchIncidents, createIncident, resolveIncident, fetchDirectory, fetchTasks } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import { overlay, success, danger } from '../../../../theme/tokens'
import { INCIDENT_SEVERITIES, severityLabel, severityIsSevere, incidentCountLine, incidentKpis } from './incidentsView'
import { taskOptionLabel, relatedTaskLine } from './tasksView'
import { formatSopDate } from './sopsView'
import { useWriteAccess } from '../../../../lib/writeAccess'

// ── Log Incident drawer (create-only — incidents are not editable) ───────────

function LogIncidentDrawer({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState('')
  const [severity, setSeverity] = useState('low')
  const [relatedEmployee, setRelatedEmployee] = useState('')
  const [relatedTaskId, setRelatedTaskId] = useState('')
  const [description, setDescription] = useState('')

  // "Related agent" picklist = live ACTIVE employees with a None blank —
  // the mockup's fillPeopleSelect(select, 'None') (:5026). "Related task"
  // = the real task list (M5a — the mockup's fillTaskSelect, :5029-5035).
  const employees = useAsync(() => fetchDirectory({ status: 'active' }), [])
  const tasks = useAsync(() => fetchTasks(), [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  async function handleSave() {
    if (!title.trim()) { toast.error('Title is required'); return }
    try {
      await createIncident({
        title: title.trim(), severity, description,
        relatedEmployee: relatedEmployee || undefined,
        relatedTaskId: relatedTaskId || undefined,
      })
      toast.success('Incident logged')
      onSaved()
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to log incident'))
    }
  }

  const inputCls = 'w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-muted transition-all'
  const labelCls = 'block text-12 font-semibold text-ink-body mb-2'

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="fixed top-0 right-0 bg-surface-card border-l border-edge-row flex flex-col"
        style={{ height: '100dvh', width: 'min(580px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">Log Incident</div>
            <div className="text-12 text-ink-muted mt-1">Something's gone wrong — track it here until it's resolved.</div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-muted hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          <div>
            <label className={labelCls}>Title<span className="text-danger ml-1">*</span></label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Missed appointment — Northpeak site visit" autoFocus className={inputCls} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Severity</label>
              <select value={severity} onChange={e => setSeverity(e.target.value)} className={inputCls}>
                {INCIDENT_SEVERITIES.map(s => <option key={s.slug} value={s.slug}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Related agent</label>
              {employees.error ? (
                <div className="text-12 text-ink-muted px-1 py-3">Couldn't load employees</div>
              ) : (
                <select value={relatedEmployee} onChange={e => setRelatedEmployee(e.target.value)}
                  disabled={employees.loading} className={inputCls}>
                  <option value="">None</option>
                  {(employees.data ?? []).map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.fullName}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div>
            <label className={labelCls}>Related task</label>
            {tasks.error ? (
              <div className="text-12 text-ink-muted px-1 py-3">Couldn't load tasks</div>
            ) : (
              <select value={relatedTaskId} onChange={e => setRelatedTaskId(e.target.value)}
                disabled={tasks.loading} className={inputCls}>
                <option value="">None</option>
                {(tasks.data ?? []).map(t => (
                  <option key={t.id} value={t.id}>{taskOptionLabel(t)}</option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className={labelCls}>Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="What happened..." rows={5}
              className={`${inputCls} resize-none`} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          <button onClick={onClose}
            className="k-btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave}
            className="k-btn">
            Log Incident
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

function SeverityBadge({ severity }: { severity: string }) {
  const severe = severityIsSevere(severity)
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-11 font-medium ${severe ? '' : 'bg-surface-panel text-ink-secondary'}`}
      style={severe ? { background: danger.bg, color: danger.text } : undefined}>
      {severityLabel(severity)}
    </span>
  )
}

function IncidentStatusBadge({ status }: { status: string }) {
  if (status === 'resolved') {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-11 font-medium"
        style={{ background: success.bg, color: success.text }}>
        Resolved
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-2 py-1 rounded-full text-11 font-medium bg-surface-panel text-ink-secondary">
      Open
    </span>
  )
}

export function OperationsIncidents() {
  const [reloadKey, setReloadKey] = useState(0)
  const [showDrawer, setShowDrawer] = useState(false)
  const { data, loading, error } = useAsync(fetchIncidents, [reloadKey])
  // Log incident and resolve are records-level writes (lib/writeAccess).
  const { records } = useWriteAccess()

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const incidents = data ?? []
  const kpis = incidentKpis(incidents, new Date())

  async function handleResolve(id: string) {
    try {
      await resolveIncident(id)
      toast.success('Incident resolved')
      setReloadKey(k => k + 1)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to resolve incident'))
    }
  }

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Incidents"
        subtitle={incidents.length === 0 ? 'Things that have gone wrong, tracked to resolution.' : incidentCountLine(incidents.length)}
        action={records ? (
          <button className="k-btn inline-flex items-center gap-2" onClick={() => setShowDrawer(true)}>
            <Plus size={14} /> Log Incident
          </button>
        ) : undefined}
      />

      <div className="grid grid-cols-4 gap-3">
        <OpsMetric label="Open" value={kpis.open} note="Needs attention" />
        <OpsMetric label="Critical / High open" value={kpis.severeOpen} note="Highest priority" />
        <OpsMetric label="Resolved this month" value={kpis.resolvedThisMonth} note="Calendar month" />
        <OpsMetric label="Avg. resolution time" value={kpis.avgResolution} note="Across resolved incidents" />
      </div>

      <div className="k-card">
        {incidents.length === 0 ? (
          <EmptyState
            message="No incidents logged"
            hint={records ? "Log anything that goes wrong and track it here until it's resolved" : 'Incidents will appear here once they are logged'}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="closr-table ops-table w-full">
              <thead>
                <tr>
                  <th className="text-left">Incident</th>
                  <th className="text-left">Severity</th>
                  <th className="text-left">Related agent</th>
                  <th className="text-left">Date</th>
                  <th className="text-left">Status</th>
                  <th className="text-left"></th>
                </tr>
              </thead>
              <tbody>
                {incidents.map(i => (
                  <tr key={i.id}>
                    <td>
                      <div className="text-13 font-medium text-ink-primary">{i.title}</div>
                      {i.description && <div className="text-11 text-ink-muted mt-1" style={{ maxWidth: 380 }}>{i.description}</div>}
                      {/* The M5a task link, rendered ONLY when linked — never
                          a column of permanent dashes (the M3 honesty rule). */}
                      {i.relatedTaskId && (
                        <div className="text-11 text-ink-muted mt-1">
                          Task: {relatedTaskLine(i.relatedTaskType, i.relatedTaskDate) || '—'}
                        </div>
                      )}
                    </td>
                    <td><SeverityBadge severity={i.severity} /></td>
                    <td className="text-12 text-ink-secondary">{i.relatedEmployeeName || '—'}</td>
                    <td className="text-12 text-ink-secondary whitespace-nowrap">{formatSopDate(i.createdAt)}</td>
                    <td><IncidentStatusBadge status={i.status} /></td>
                    <td className="whitespace-nowrap">
                      {i.status === 'open' && records && (
                        <button className="k-btn-secondary" onClick={() => handleResolve(i.id)}>
                          Mark Resolved
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showDrawer && records && (
        <LogIncidentDrawer
          onClose={() => setShowDrawer(false)}
          onSaved={() => setReloadKey(k => k + 1)}
        />
      )}
    </div>
  )
}
